import {
  useSalesServiceSalesOrdersMediaDestroy,
  useSalesServiceSalesOrdersMediaList,
  useSalesServiceSalesOrdersMediaListKey
} from '@insurance/api/queries'
import { OrderMediaDetail as OrderMediaType } from '@insurance/api/requests'
import { ConditionalText } from '@shared/components'
import { WithPagination } from '@shared/types'
import { showApiErrors } from '@shared/utils'
import { useQueryClient } from '@tanstack/react-query'
import { App, Button, Flex, List, ListProps, Tag } from 'antd'
import { Trash } from 'iconsax-react'
import { useTranslation } from 'react-i18next'

import { DownloadButton } from '../DownloadButton'
import { PreviewMedia } from './PreviewMedia'

type Props = {
  orderId?: string | number
  hiddenDelete?: boolean
  disabledFileGet?: boolean
  /**
   * Opt-in: when given, each row shows the document's category as a tag. Only
   * products whose case file distinguishes kinds pass it — for everyone else
   * every attachment is an `order` and the tag would be noise.
   */
  categoryLabelKey?: Record<string, string>
} & ListProps<OrderMediaType>

export const OrderMedia = ({
  orderId,
  hiddenDelete,
  disabledFileGet,
  categoryLabelKey,
  ...props
}: Props) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { message, modal } = App.useApp()

  const { mutateAsync: deleteMedia } = useSalesServiceSalesOrdersMediaDestroy({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [useSalesServiceSalesOrdersMediaListKey]
      })

      message.success(t('removed_successfully'))
    },
    onError: (error) => showApiErrors(error)
  })

  const { data: orderMedia } = useSalesServiceSalesOrdersMediaList(
    { order: Number(orderId) },
    undefined,
    { enabled: !!orderId }
  )

  const mediaFromCache = queryClient.getQueryData([
    useSalesServiceSalesOrdersMediaListKey,
    { order: Number(orderId) }
  ]) as WithPagination<OrderMediaType>

  const data = disabledFileGet ? mediaFromCache?.results : orderMedia?.results

  const onDeleteMedia = (id?: number, name?: string) => {
    if (!id) return

    modal.confirm({
      title: t('delete_confirm'),
      content: (
        <div
          dangerouslySetInnerHTML={{
            __html: t('delete_confirm_description_media', { name })
          }}
        />
      ),
      onOk: () =>
        deleteMedia({
          id
        })
    })
  }

  return (
    <List
      dataSource={data}
      renderItem={({ media, notes, id }) => (
        <List.Item
          extra={<PreviewMedia fileMedia={media} />}
          actions={[
            <DownloadButton file={media} />,
            ...(!hiddenDelete
              ? [
                  <Button
                    type="link"
                    icon={<Trash size="16" />}
                    onClick={() => onDeleteMedia(id, media?.name || '')}
                  >
                    {t('delete')}
                  </Button>
                ]
              : [])
          ]}
        >
          <List.Item.Meta
            title={
              <Flex align="center" gap={8} wrap>
                {media?.name}

                {!!categoryLabelKey && !!media?.category && (
                  // `MediaCategory` is wider than the set any one product
                  // offers on upload (it also carries policy/user/invoice/
                  // claim), so an unmapped value falls back to its raw code
                  // rather than rendering an empty tag.
                  <Tag color="processing">
                    {categoryLabelKey[media.category]
                      ? t(categoryLabelKey[media.category])
                      : media.category}
                  </Tag>
                )}
              </Flex>
            }
            description={
              <ConditionalText condition={!!notes}>{notes}</ConditionalText>
            }
          />
        </List.Item>
      )}
      {...props}
    />
  )
}
